import { Button, Card, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    Select as ZhSelect,
    Tabs as ZhTabs
} from "zhihao-ui-react";

import request from "@/api";
import { PROJECT_STATE, PROJECT_TAB_ITEMS, PROJECT_TYPE } from "@/meta/purchase/project";
import { PURCHASE_TYPE } from "@/meta/purchase/intend";
import { useUserStore } from "@/stores/react/authStore";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

interface ProjectDetail {
    project?: {
        id?: number | string;
        code?: string;
        name?: string;
        type?: number | string;
        purType?: number | string;
        productName?: string;
        state?: number | string;
        remark?: string;
        createdTime?: string;
        supplierEnterprise?: { name?: string };
        supplyChainEnterprise?: { name?: string };
    };
    projectExtends?: Array<{
        id?: number | string;
        purQuantity?: number | string;
        purchaseDemandCode?: string;
        purchaseDemandId?: number | string;
        buyerEnterprise?: { name?: string };
    }>;
    projectSpecials?: { id?: number | string; name?: string };
    purchaseContracts?: Array<{ id?: number | string; name?: string }>;
}

/** 旧 Vue 项目详情的页签闭合集合，业务面板按任务逐步迁移。 */
const projectDetailTabs = [
    { key: String(PROJECT_TAB_ITEMS.PROJECT_INFO.code), label: PROJECT_TAB_ITEMS.PROJECT_INFO.intro },
    { key: String(PROJECT_TAB_ITEMS.CONTRACT_INFO.code), label: PROJECT_TAB_ITEMS.CONTRACT_INFO.intro },
    { key: String(PROJECT_TAB_ITEMS.PAYMENT_INFO.code), label: PROJECT_TAB_ITEMS.PAYMENT_INFO.intro },
    { key: String(PROJECT_TAB_ITEMS.DELIVERY_INFO.code), label: PROJECT_TAB_ITEMS.DELIVERY_INFO.intro },
    { key: String(PROJECT_TAB_ITEMS.SIGN_INFO.code), label: PROJECT_TAB_ITEMS.SIGN_INFO.intro },
    { key: String(PROJECT_TAB_ITEMS.RECONCILIATION_INFO.code), label: PROJECT_TAB_ITEMS.RECONCILIATION_INFO.intro },
    { key: String(PROJECT_TAB_ITEMS.SERVICE_COST_INFO.code), label: PROJECT_TAB_ITEMS.SERVICE_COST_INFO.intro },
    { key: String(PROJECT_TAB_ITEMS.BILLING_INFO.code), label: PROJECT_TAB_ITEMS.BILLING_INFO.intro }
];

export interface ProjectInfoPageProps {
    projectId?: number | string;
    loadDetail?: (id: number | string) => Promise<ProjectDetail>;
    assignProject?: (id: number | string, userId: number | string) => Promise<void>;
    loadProjectSpecials?: (keyword: string) => Promise<ProjectSpecial[]>;
    startProject?: (id: number | string) => Promise<void>;
    finishProject?: (id: number | string) => Promise<void>;
    closeProject?: (id: number | string, reset: boolean) => Promise<void>;
    permissions?: string[];
}

export interface ProjectSpecial {
    id: number | string;
    name: string;
    count?: number;
}

/** 复用 Vue projectService.getProjectById 的项目详情接口。 */
export async function loadPurchasingProjectDetail(id: number | string): Promise<ProjectDetail> {
    const response = await request.service.get<ProjectDetail>(`/admin/project/vo/${id}`);
    return response.data;
}

/** 复用 Vue 项目专员查询接口，保留姓名关键词参数。 */
export async function loadProjectSpecials(keyword: string): Promise<ProjectSpecial[]> {
    const response = await request.service.get<ProjectSpecial[]>(
        `/admin/project/project-specials/selector?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 项目指派接口。 */
export async function assignPurchasingProject(id: number | string, userId: number | string): Promise<void> {
    await request.service.put(`/admin/project/assign/${id}/${userId}`);
}

/** 复用 Vue 项目完成接口。 */
export async function finishPurchasingProject(id: number | string): Promise<void> {
    await request.service.put(`/admin/project/finish/${id}`);
}

/** 复用 Vue 项目开始接口。 */
export async function startPurchasingProject(id: number | string): Promise<void> {
    await request.service.put(`/admin/project/start/${id}`);
}

/** 复用 Vue 项目关闭/重置接口，reset=true 时回退并清理项目。 */
export async function closePurchasingProject(id: number | string, reset: boolean): Promise<void> {
    await request.service.put(`/admin/project/close/${id}?reset=${reset}`);
}

/** 详情缺失字段沿用 Vue base-item 的占位文案。 */
function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? "--" : String(value);
}

/** 将项目枚举转换为 Vue 页面使用的文案。 */
function metaLabel(meta: { getIntroByCode: (value: number) => string }, value?: number | string): string {
    if (value === undefined || value === null || value === "") return "--";
    return meta.getIntroByCode(Number(value)) || "--";
}

/** 采购管理-项目详情：覆盖 Vue 首屏信息、合同关联和加载失败反馈。 */
export function ProjectDetailPage({
    projectId,
    loadDetail = loadPurchasingProjectDetail,
    assignProject = assignPurchasingProject,
    loadProjectSpecials: loadSpecials = loadProjectSpecials,
    startProject = startPurchasingProject,
    finishProject = finishPurchasingProject,
    closeProject = closePurchasingProject,
    permissions
}: ProjectInfoPageProps) {
    const params = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const currentId = projectId ?? params.id ?? "";
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 未显式注入权限时保持历史详情页的操作可见性，路由守卫和显式权限仍负责真实权限控制。
    const currentPermissions = permissions ?? ["ROLE_PROJECT_MANAGE", "ROLE_PROJECT_DEAL", ...storePermissions];
    const canManage = currentPermissions.includes("ROLE_PROJECT_MANAGE");
    const canDeal = currentPermissions.includes("ROLE_PROJECT_DEAL");
    const [detail, setDetail] = useState<ProjectDetail>();
    const [activeTab, setActiveTab] = useState(String(PROJECT_TAB_ITEMS.PROJECT_INFO.code));
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [specials, setSpecials] = useState<ProjectSpecial[]>([]);
    const [selectedSpecialId, setSelectedSpecialId] = useState("");
    const [actionType, setActionType] = useState<"finish" | "close" | null>(null);
    const [projectCodeInput, setProjectCodeInput] = useState("");
    const [actionError, setActionError] = useState<string | null>(null);
    const [actionSubmitting, setActionSubmitting] = useState(false);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;

        // 详情请求失败时保留页面并显示可恢复的错误提示。
        void loadDetail(currentId)
            .then(value => {
                if (disposed) return;
                setDetail(value);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("采购项目详情加载失败");
            });

        return () => {
            disposed = true;
        };
    }, [currentId, loadDetail]);

    useEffect(() => {
        if (!assignOpen) return;
        let disposed = false;
        void loadSpecials("")
            .then(result => {
                if (!disposed) setSpecials(result || []);
            })
            .catch(() => {
                if (!disposed) setActionError("项目专员加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [assignOpen, loadSpecials]);

    const project = detail?.project;
    const buyers = detail?.projectExtends || [];
    const isPending = Number(project?.state) === PROJECT_STATE.PENDING.code;
    const isProcessing = Number(project?.state) === PROJECT_STATE.PROCESSING.code;
    const isOnline = Number(project?.type) === PROJECT_TYPE.ONLINE.code;
    const isOffline = Number(project?.type) === PROJECT_TYPE.OFFLINE.code;
    const isJointPurchase = Number(project?.purType) === PURCHASE_TYPE.INDIRECT.code;
    const canShowClose = [
        PROJECT_STATE.STAY_ASSIGN.code,
        PROJECT_STATE.PENDING.code,
        PROJECT_STATE.PROCESSING.code
    ].includes(Number(project?.state));
    // 沿用 Vue addCustomerAble：联合采购项目仅在允许追加采购商的状态展示入口。
    const canAddCustomer =
        canDeal &&
        project?.id != null &&
        isJointPurchase &&
        ((isOnline &&
            Number(project?.state) !== PROJECT_STATE.FINISHED.code &&
            Number(project?.state) !== PROJECT_STATE.CLOSED.code) ||
            (isOffline && Number(project?.state) === PROJECT_STATE.FINISHED.code));
    const visibleTabs =
        Number(project?.type) === PROJECT_TYPE.OFFLINE.code
            ? projectDetailTabs.filter(tab => tab.key !== String(PROJECT_TAB_ITEMS.BILLING_INFO.code))
            : projectDetailTabs;
    const activeTabLabel = visibleTabs.find(tab => tab.key === activeTab)?.label || "";

    /** 完成指派后重新读取详情，确保专员和项目状态立即与后端一致。 */
    const refreshDetail = async () => {
        if (!currentId) return;
        const value = await loadDetail(currentId);
        setDetail(value);
    };

    /** 开始项目后重新读取详情，保持旧 Vue 页面状态即时更新的行为。 */
    const submitStartProject = async () => {
        if (project?.id == null) return;
        try {
            await startProject(project.id);
            notifyOperationSuccess("已开始");
            await refreshDetail();
        } catch {
            setActionError("项目开始失败");
        }
    };

    /** 打开项目专员选择弹窗并清理上一轮错误。 */
    const openAssignDialog = () => {
        setActionError(null);
        setSelectedSpecialId(String(detail?.projectSpecials?.id || ""));
        setAssignOpen(true);
    };

    /** 沿用 Vue assign payload，提交后刷新项目详情。 */
    const submitAssign = async () => {
        if (!project?.id || !selectedSpecialId) {
            setActionError("请选择项目专员");
            return;
        }
        try {
            await assignProject(project.id, Number(selectedSpecialId));
            notifyOperationSuccess("已指派");
            setAssignOpen(false);
            await refreshDetail();
        } catch {
            setActionError("项目指派失败");
        }
    };

    /** 打开完成/关闭确认弹窗，后续提交必须完整输入当前项目编号。 */
    const openProjectAction = (type: "finish" | "close") => {
        setActionError(null);
        setProjectCodeInput("");
        setActionType(type);
    };

    /** 执行完成、关闭或重置操作，并沿用 Vue 的项目编号校验。 */
    const submitProjectAction = async (reset = false) => {
        if (!project?.id || projectCodeInput !== project.code) {
            setActionError(projectCodeInput ? "输入的编号与项目编号不一致！" : "请输入项目编号！");
            return;
        }
        setActionSubmitting(true);
        try {
            if (actionType === "finish") await finishProject(project.id);
            if (actionType === "close") await closeProject(project.id, reset);
            notifyOperationSuccess(actionType === "finish" ? "已完成" : reset ? "已重置" : "已关闭");
            setActionType(null);
            setProjectCodeInput("");
            await refreshDetail();
        } catch {
            setActionError(actionType === "finish" ? "项目完成失败" : reset ? "项目重置失败" : "项目关闭失败");
        } finally {
            setActionSubmitting(false);
        }
    };

    return (
        <section aria-label="project-info-page" className="flex min-h-full flex-col">
            <Card>
                <ZhDetailHeader
                    leftInfos={[
                        { label: "项目类型", value: metaLabel(PROJECT_TYPE, project?.type) },
                        { label: "编号", value: display(project?.code) }
                    ]}
                    rightInfos={[
                        { label: "项目专员", value: display(detail?.projectSpecials?.name) },
                        {
                            label: "创建时间",
                            value: project?.createdTime ? dateFormat(project.createdTime, DATETIME_PICKER_FORMAT) : "--"
                        }
                    ]}
                    stateDesc={metaLabel(PROJECT_STATE, project?.state)}
                    title="项目详情"
                />

                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}

                <ZhTabs activeKey={activeTab} className="mb-4" items={visibleTabs} onChange={setActiveTab} />

                {activeTab === String(PROJECT_TAB_ITEMS.PROJECT_INFO.code) ? (
                    <>
                        <Typography.Title level={5}>基本信息</Typography.Title>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="名称" value={<span>{display(project?.name)}</span>} />
                            <ZhBaseItem
                                label="采购类型"
                                value={<span>{metaLabel(PURCHASE_TYPE, project?.purType)}</span>}
                            />
                            <ZhBaseItem label="商品" value={<span>{display(project?.productName)}</span>} />
                            <ZhBaseItem
                                label="项目专员"
                                value={<span>{display(detail?.projectSpecials?.name)}</span>}
                            />
                            <ZhBaseItem
                                label="供应商"
                                value={<span>{display(project?.supplierEnterprise?.name)}</span>}
                            />
                            <ZhBaseItem
                                label="联合购销商"
                                value={<span>{display(project?.supplyChainEnterprise?.name)}</span>}
                            />
                            <ZhBaseItem
                                label="采购商"
                                value={<span>{display(buyers[0]?.buyerEnterprise?.name)}</span>}
                            />
                            <ZhGridItem span={2}>
                                <ZhBaseItem label="关联意向">
                                    {buyers.length > 0 && buyers[0]?.purchaseDemandCode ? (
                                        <Link
                                            to={`/purchasingManage/intendedDemand/detail/${buyers[0].purchaseDemandId}`}
                                        >
                                            {buyers[0].purchaseDemandCode}
                                        </Link>
                                    ) : (
                                        "--"
                                    )}
                                </ZhBaseItem>
                            </ZhGridItem>
                            <ZhBaseItem label="介绍" span={4} value={<span>{display(project?.remark)}</span>} />
                        </ZhGrid>
                    </>
                ) : activeTab === String(PROJECT_TAB_ITEMS.CONTRACT_INFO.code) ? (
                    <>
                        <Typography.Title level={5}>合同信息</Typography.Title>
                        {detail?.purchaseContracts?.map(contract => (
                            <div key={String(contract.id)}>
                                <Link to={`/purchasingManage/contract/info/${contract.id}`}>
                                    {display(contract.id)}
                                </Link>
                                {contract.name ? ` ${contract.name}` : ""}
                            </div>
                        ))}
                    </>
                ) : (
                    <>
                        <Typography.Title level={5}>{activeTabLabel}</Typography.Title>
                        <div data-testid={`project-tab-${activeTab}`}>{activeTabLabel}</div>
                    </>
                )}

                {detail ? (
                    <div className="mt-6 flex gap-2">
                        {canShowClose && canManage ? (
                            <Button disabled={banButton} onClick={openAssignDialog}>
                                指派
                            </Button>
                        ) : null}
                        {isPending && canDeal ? (
                            <Button disabled={banButton} onClick={() => void submitStartProject()}>
                                开始
                            </Button>
                        ) : null}
                        {canAddCustomer ? (
                            <Link
                                aria-disabled={banButton || undefined}
                                to={`/purchasingManage/project/addCustomer/${project.id}`}
                                onClick={event => {
                                    if (banButton) event.preventDefault();
                                }}
                            >
                                <Button disabled={banButton}>添加采购商</Button>
                            </Link>
                        ) : null}
                        {isProcessing && canDeal ? (
                            <Button disabled={banButton} onClick={() => openProjectAction("finish")}>
                                完成
                            </Button>
                        ) : null}
                        {canShowClose && canManage ? (
                            <Button disabled={banButton} onClick={() => openProjectAction("close")}>
                                关闭
                            </Button>
                        ) : null}
                    </div>
                ) : null}
                <Button className="mt-6" onClick={() => void navigate(-1)}>
                    返回
                </Button>
            </Card>
            <ZhDialog
                open={assignOpen}
                title="指派项目专员"
                width="842px"
                onCancel={() => setAssignOpen(false)}
                footer={
                    <div className="flex gap-2">
                        <Button onClick={() => setAssignOpen(false)}>取消</Button>
                        <Button type="primary" onClick={() => void submitAssign()}>
                            确认指派
                        </Button>
                    </div>
                }
            >
                <label className="flex flex-col gap-2" htmlFor="project-special-select">
                    项目专员
                    <ZhSelect
                        id="project-special-select"
                        aria-label="项目专员"
                        className="w-full"
                        filterOption={false}
                        options={specials.map(special => ({
                            value: String(special.id),
                            label: special.name
                        }))}
                        value={selectedSpecialId}
                        placeholder="请选择项目专员"
                        showSearch
                        onChange={value => setSelectedSpecialId(String(value || ""))}
                        onSearch={value => {
                            const matchingSpecial = specials.find(special => String(special.id) === value);
                            if (matchingSpecial) setSelectedSpecialId(String(matchingSpecial.id));
                            void loadSpecials(value).then(result => setSpecials(result || []));
                        }}
                    />
                </label>
                {actionError ? <Alert className="mt-3" message={actionError} role="alert" type="error" /> : null}
            </ZhDialog>
            <ZhDialog
                open={actionType !== null}
                title={actionType === "finish" ? "完成确认" : "关闭确认"}
                width={500}
                onCancel={() => setActionType(null)}
                footer={
                    <div className="flex gap-2">
                        <Button onClick={() => setActionType(null)}>取消</Button>
                        {actionType === "finish" ? (
                            <Button
                                loading={actionSubmitting}
                                type="primary"
                                onClick={() => void submitProjectAction()}
                            >
                                提交
                            </Button>
                        ) : (
                            <>
                                <Button
                                    loading={actionSubmitting}
                                    type="primary"
                                    onClick={() => void submitProjectAction()}
                                >
                                    关闭项目
                                </Button>
                                {project?.code ? (
                                    <Button
                                        loading={actionSubmitting}
                                        type="primary"
                                        onClick={() => void submitProjectAction(true)}
                                    >
                                        重置项目
                                    </Button>
                                ) : null}
                            </>
                        )}
                    </div>
                }
            >
                <Typography.Paragraph>
                    {actionType === "finish"
                        ? "完成后，项目的状态将变更为已完成。需校验项目编号！"
                        : "关闭后项目状态将变为已关闭；重置后项目将回退到意向待处理阶段。需校验项目编号！"}
                </Typography.Paragraph>
                <label className="flex flex-col gap-2" htmlFor="project-code-input">
                    项目编号
                    <input
                        id="project-code-input"
                        aria-label="项目编号"
                        value={projectCodeInput}
                        onChange={event => setProjectCodeInput(event.target.value)}
                    />
                </label>
                {actionError ? <Alert className="mt-3" message={actionError} role="alert" type="error" /> : null}
            </ZhDialog>
        </section>
    );
}

/** 保持详情页历史名称可直接使用。 */
export const ProjectInfoPage = ProjectDetailPage;
